<template>
  <div class="bg-white h-auto rounded-lg">
    <div
      class="flex flex-col lg:flex-row md:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <Heading heading="Exams" heading-size="text-[18px]" heading-padding="py-0" />
        <div
          v-permission="EXAMS_TABLE_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse sm:flex-nowrap gap-2 items-end justify-end"
      >
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusList"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
        />
        <CalendarActionsWrapper
          :is-future-date-disabled="true"
          :date-format="DATETIME_CONSTANT.SHORT_MONTH_FULL_YEAR"
          :calendar-date="selectedDate"
          filter-view="Month"
          custom-class="right-0 before:right-12"
          @selectDate="onDateChange"
        />
      </div>
    </div>
    <div>
      <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
      <NoRecordFound v-else-if="!isLoading && isEmpty(examsList)" />
      <TableWrapper v-else>
        <THead>
          <TRHead>
            <TH v-for="head in tableHeaders" :key="head.id">
              {{ head }}
            </TH>
          </TRHead>
        </THead>
        <TBody>
          <TRBody v-for="(exam, index) in examsList" :key="index">
            <TD>
              <span class="cursor-pointer text-primary-purple-600">
                {{ exam.title }}
              </span>
            </TD>
            <TD>
              {{ formatDateFromLocalToUTC(exam.start_date, DATETIME_CONSTANT.DATE) }}
            </TD>
            <TD>
              {{ formatDateFromLocalToUTC(exam.end_date, DATETIME_CONSTANT.DATE) }}
            </TD>
            <TD>
              {{ exam.total_students }}
            </TD>
            <TD v-permission="campusLevelPermisions">{{ exam.class_title }}</TD>
            <TD v-permission="classLevelPermisions">
              {{ exam.section_title }}
            </TD>

            <TD>
              <UiStatus :title="exam.status" />
            </TD>
            <TD>
              <UiProgressBar :percentage="exam.result_rate" />
            </TD>
          </TRBody>
        </TBody>
      </TableWrapper>
    </div>
    <div class="pb-6 flex mt-2">
      <Pagination
        v-if="showPagination"
        v-show="!isLoading"
        :key="forceRender"
        :record-limit="GENERAL_CONSTANTS.RECORD_LIMIT"
        class="flex-1"
        :max-range="paginationCounts(examCounts, GENERAL_CONSTANTS.RECORD_LIMIT)"
        @filterRecord="fetchExamsList"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import Loader from '@components/BaseComponent/Loader.vue'
import Heading from '@/src/components/Heading.vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import CalendarActionsWrapper from '@src/components/Calendar/CalendarActionsWrapper.vue'
import icon from '@src/components/icons/icon.vue'
import { formatDateFromLocalToUTC } from '@src/utils/moment.util.js'
import { useStore } from 'vuex'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import UiProgressBar from '@src/components/UiElements/UiProgressBar.vue'
import UiStatus from '@src/components/UiElements/UiStatus.vue'
import {
  paginationCounts,
  paginationRangeHandler,
} from '@src/components/BaseComponent/pagination.util.js'
import Pagination from '@components/BaseComponent/Pagination.vue'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'
import isEmpty from 'lodash/isEmpty'
import NoRecordFound from '@src/components/BaseComponent/NoRecordFound.vue'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import { SCOPE_LEVELS, ENV } from '@src/constants/general-constants'
import { getCurrentScopeFlags } from '@/src/utils/level-scope.util.js'

// Store and reactive data initialization
const store = useStore()

//data
const dateRange = ref({
  startDate: null,
  endDate: null,
})
const examCounts = ref(0)
const showPagination = ref(false)

const isLoading = ref(false)
const selectedCampus = ref('')
const selectedClass = ref('')
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const currentClassScope = computed(() => store.state.layout.currentClassScope)
const currentSectionScope = computed(() => store.state.layout.currentSectionScope)
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const campusList = computed(() => store.state.layout.campuses)
// Reactive object for graph data
const tableHeaders = computed(() => {
  const { isClassLevel, isSectionLevel } = getCurrentScopeFlags()
  const baseHeaders = ['Exam Name', 'Start Date', 'End Date', 'Total Students']
  if (isClassLevel) {
    return [...baseHeaders, 'Section', 'Status', 'Result Rate']
  } else if (isSectionLevel) {
    return [...baseHeaders, 'Status', 'Result Rate']
  } else {
    return [...baseHeaders, 'Class', 'Section', 'Status', 'Result Rate']
  }
})
const examsList = ref([])
const EXAMS_TABLE_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}
const selectedDate = ref(new Date())
const campusLevelPermisions = {
  basedOn: [SCOPE_LEVELS.ROOT_LEVEL, SCOPE_LEVELS.CAMPUS_LEVEL],
}
const classLevelPermisions = {
  basedOn: [SCOPE_LEVELS.ROOT_LEVEL, SCOPE_LEVELS.CAMPUS_LEVEL, SCOPE_LEVELS.CLASS_LEVEL],
}
// Initialize on mount
onMounted(() => {
  generateExamsList()
})
// Watchers for reactivity
watch(
  [currentCampusScope, currentClassScope, currentSectionScope, selectedDate, selectedCampus],
  () => {
    fetchExamsList()
  },
)
const fetchExamsList = async (range) => {
  isLoading.value = true
  if (currentCampusScope.value) {
    selectedCampus.value = currentCampusScope.value.id
  }
  if (selectedCampus.value) {
    const paginationPayload = paginationRangeHandler(range)
    const month = selectedDate.value.getMonth() + 1
    const year = selectedDate.value.getFullYear()

    const currentDate = new Date()
    const pastWeekDate = new Date()
    pastWeekDate.setDate(currentDate.getDate() - 6)

    dateRange.value.startDate = pastWeekDate
    dateRange.value.endDate = currentDate
    const campusFilter = currentCampusScope.value
      ? {}
      : {
          $where: {
            ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value),
          },
        }
    const payload = {
      month,
      year,
      ...campusFilter,
      ...paginationPayload,
    }

    try {
      const response = await getExamsStats(payload)
      if (response) {
        examsList.value = response.exam_stats
        examCounts.value = response.meta.total_records
        showPagination.value = response.meta.total_records > GENERAL_CONSTANTS.RECORD_LIMIT
      }
    } catch (error) {
      examsList.value = []
    } finally {
      isLoading.value = false
    }
  }
}
const onDateChange = (date) => {
  selectedDate.value = date
}

// Generate attendance stats for sections
const generateExamsList = async () => {
  if (currentCampusScope.value) {
    selectedCampus.value = currentCampusScope.value.id
  }
  if (currentClassScope.value) {
    selectedClass.value = currentClassScope.value.id
  }
  const currentDate = new Date()
  const pastWeekDate = new Date()
  pastWeekDate.setDate(currentDate.getDate() - 6)

  dateRange.value.startDate = pastWeekDate
  dateRange.value.endDate = currentDate
}

// API calls
const getExamsStats = (payload) => store.dispatch('instituteCount/getExamsTableStats', payload)
</script>
